import { useState, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { Leaf, Menu, X } from "lucide-react";

export default function Header({ onOpenModal }) {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "#features", label: t.nav.features },
    { href: "#pricing", label: t.nav.pricing },
    { href: "#faq", label: t.nav.faq },
  ];

  const handleCta = () => {
    if (onOpenModal) onOpenModal();
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#D1FAE5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-2 group"
        >
          <span className="w-9 h-9 rounded-2xl bg-[#22C55E] flex items-center justify-center shadow-sm shadow-green-900/20 group-hover:rotate-[-8deg] transition-transform duration-300">
            <Leaf className="w-5 h-5 text-white" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-2xl font-black tracking-tight text-[#064E3B]">
            staysy
            <span className="text-[#22C55E]">.</span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.href.slice(1)}-link`}
              className="font-body text-[15px] font-medium text-[#047857] hover:text-[#064E3B] transition-colors relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#22C55E] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300 rounded-full" />
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div
            data-testid="lang-switcher"
            className="flex items-center bg-white border border-[#D1FAE5] rounded-full p-1 shadow-sm"
          >
            <button
              data-testid="lang-cs-btn"
              onClick={() => lang !== "cs" && toggle()}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                lang === "cs"
                  ? "bg-[#064E3B] text-white shadow"
                  : "text-[#047857] hover:text-[#064E3B]"
              }`}
            >
              CS
            </button>
            <button
              data-testid="lang-en-btn"
              onClick={() => lang !== "en" && toggle()}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                lang === "en"
                  ? "bg-[#064E3B] text-white shadow"
                  : "text-[#047857] hover:text-[#064E3B]"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#login"
            data-testid="header-login-link"
            className="hidden md:inline-block font-body text-[15px] font-medium text-[#047857] hover:text-[#064E3B] transition-colors"
          >
            {t.nav.login}
          </a>

          <button
            data-testid="header-cta-btn"
            onClick={handleCta}
            className="hidden md:inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#064E3B] text-white font-body font-semibold text-sm shadow-lg shadow-green-900/20 hover:-translate-y-0.5 hover:shadow-xl hover:bg-[#22C55E] transition-all duration-300"
          >
            {t.nav.cta}
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full bg-white border border-[#D1FAE5] flex items-center justify-center text-[#064E3B]"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#D1FAE5]"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${item.href.slice(1)}-link`}
                className="font-body text-base font-medium text-[#047857] py-2"
              >
                {item.label}
              </a>
            ))}
            <button
              data-testid="mobile-cta-btn"
              onClick={() => {
                handleCta();
                setMobileOpen(false);
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#064E3B] text-white font-body font-semibold"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
